import { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
// @ts-ignore
import { BubbleMenu } from '@tiptap/react/menus';
import { CharacterCount } from '@tiptap/extensions';
import StarterKit from '@tiptap/starter-kit';
import './index.css';
import { Button, Group, Modal, TextInput, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

interface FeebackContentEditorProps {
  value?: string;
}

interface FeedbackContentEditorRef {
  getHTML: () => string;
}

const limit = 150;

export default forwardRef<FeedbackContentEditorRef, FeebackContentEditorProps>(({ value }, ref) => {
  const [link, setLink] = useState('');
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();

  const editor = useEditor({
    extensions: [
      StarterKit,
      CharacterCount.configure({
        limit,
      }),
    ],
    content: value,
  });
  const { charactersCount, wordsCount } = useEditorState({
    editor,
    selector: (context) => ({
      charactersCount: context.editor.storage.characterCount.characters(),
      wordsCount: context.editor.storage.characterCount.words(),
    }),
  });

  const onLinkClick = () => {
    const isLink = editor.isActive('link');
    if (isLink) {
      editor.commands.unsetLink();
      return;
    }
    open();
  };

  useImperativeHandle(
    ref,
    () => ({
      getHTML: () => {
        const html = editor?.getHTML() || '';
        if (html.trim() === '<p></p>') {
          return '';
        }
        return html;
      },
    }),
    [editor]
  );

  useEffect(() => {
    editor?.commands.setContent(value || '');
  }, [value]);

  return (
    <>
      {editor && (
        <BubbleMenu className="bg-white border border-gray-100 rounded-xl shadow-lg flex p-2" editor={editor}>
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`px-2 py-1 rounded-lg hover:bg-gray-300 ${
              editor.isActive('bold') ? 'bg-purple-500 hover:bg-purple-500' : ''
            }`}
          >
            {t('settings.feedback.bold')}
          </button>
          <button
            onClick={onLinkClick}
            className={`px-2 py-1 rounded-lg hover:bg-gray-300 ${
              editor.isActive('link') ? 'bg-purple-500 hover:bg-purple-500' : ''
            }`}
          >
            {t('settings.feedback.link')}
          </button>
        </BubbleMenu>
      )}
      <EditorContent editor={editor} className="bg-white [&_.ProseMirror]:outline-none p-3 rounded-lg" />
      <Text size={'xs'} c="gray.5" mt="xs">
        {charactersCount} / {limit} {t('settings.feedback.characters')}
      </Text>
      <Modal opened={opened} onClose={close} title={t('settings.feedback.link')}>
        <TextInput
          placeholder={t('settings.feedback.link_placeholder')}
          value={link}
          onChange={(event) => {
            setLink(event.currentTarget.value.trim());
          }}
        />
        <Group justify="flex-end" mt="md">
          <Button
            disabled={!link}
            onClick={() => {
              let newLink = link;

              if (link && !link.startsWith('http://') && !link.startsWith('https://')) {
                newLink = 'https://' + link;
              }
              editor.commands.setLink({ href: newLink, target: '_blank' });
              close();
            }}
          >
            {t('settings.feedback.confirm')}
          </Button>
        </Group>
      </Modal>
    </>
  );
});
